import React, { Component } from 'react';
import { Typography, Box, Container, Popover, RadioGroup, FormControlLabel, Radio, TextField, Button } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Resource } from '../models/Resource';
import { Service } from '../models/Service';

/**
 * This component is a popover that allows the user to select a resource and slot type for a new slot.
 * 
 * The user can supply the following props:
 * 
 * isOpen (boolean): True if the popover is open, false otherwise
 * resources (array of Resource): The resources to choose from
 * selectedResource (Resource): The resource that is selected (optional, default is the first resource)
 * cancelCallback (function): A callback function that is called when the user cancels
 * okCallback (function): A callback function that is called when the user clicks OK, with the selected resource name and slot type as parameters
 *          The slot type is one of 'Booking', 'Block', 'Event'
 */
export class NewSlotSelectionPopover extends Component {

    styles = {
        button: {
            marginLeft: 10,
            marginRight: 10
        },
        container: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 20,
            marginBottom: 10
        }
    };

    state = {
        selectedResource: null,
        slotTypeIndex: -1,
    }
 
    _handleRadioChange = (event) => {
        this.setState({ slotTypeIndex: parseInt(event.target.value) });
    }


    static AllTypes = {
        EVENT:            {enum: 'EVENT', index: 1, name: 'Event', description: 'An scheduled Event on a resource'},
        MULTIDAYEVENT:    {enum: 'MULTIDAYEVENT', index: 2, name: 'Multi-day Event', description: 'An scheduled Event on a resource that recurs across multiple days'},
        BOOKING:          {enum: 'BOOKING', index: 3, name: 'Booking', description: 'A manually created Booking for a patron'},
        BLOCK:            {enum: 'BLOCK', index: 4, name: 'Block', description: 'Time that the Resource or Service is unavailable for scheduling'},
        AVAILABILITY:     {enum: 'AVAILABILITY', index: 5, name: 'Availability', description: 'Expanded availability for a Service, outside normal hours'},
    }


    _availableSlotTypes = (selectedResource) => {
  
        if (selectedResource instanceof Service)
            return [NewSlotSelectionPopover.AllTypes.BOOKING, NewSlotSelectionPopover.AllTypes.BLOCK, NewSlotSelectionPopover.AllTypes.AVAILABILITY];
        else if (selectedResource instanceof Resource)
            return [NewSlotSelectionPopover.AllTypes.EVENT, NewSlotSelectionPopover.AllTypes.MULTIDAYEVENT, NewSlotSelectionPopover.AllTypes.BLOCK];
        else {
            return [];
        }
    }

    // Use the selected resource from the state, if it exists, otherwise use the selected resource from the props, otherwise use the first resource
    _selectedResource = () => {
        const selected = this.state.selectedResource;
        if (selected && this.props.resources.includes(selected))    // we have a selected resource and our list of resources contains it
            return selected;

        if (this.props.selectedResource)            // we have one from the props
            return this.props.selectedResource;

        return this.props.resources[0];     // default to the first resource
    }

    // If the selected slot type is not in the available slot types, set it to the first available slot type
    _selectedSlotType = () => {
        const availableSlotTypes = this._availableSlotTypes(this._selectedResource());
        for (const slotType of availableSlotTypes) {
            if (slotType.index === this.state.slotTypeIndex)
                return slotType;
        }
        return availableSlotTypes[0];
    }

    _cancel = () => {
        this.props.cancelCallback();
    }

    _ok = () => {
        this.props.okCallback(this._selectedResource(), this._selectedSlotType().enum);
    }

    render() {

        let maxWidth = window.innerWidth * 0.7;  //half the window, up to 700
        if (maxWidth > 900)
            maxWidth = 900;

        const selectedResource = this._selectedResource();
        const availableSlotTypes = this._availableSlotTypes(selectedResource);
        const selectedSlotType = this._selectedSlotType();

        const sortedResources = this.props.resources.slice().sort((a, b) => {
            // Sort by type: "Resource" first, then others
            if (a.type === "Resource" && b.type !== "Resource") return -1;
            if (a.type !== "Resource" && b.type === "Resource") return 1;

            // If types are the same, sort by name (case-insensitive)
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) 
                return -1;
            if (nameA > nameB) 
                return 1;

            return 0;
        });
        
        return (
            <Popover open={this.props.isOpen}
                anchorReference="anchorPosition"
                anchorPosition={{left: window.innerWidth/2, top: window.innerHeight/2}}
                transformOrigin={{vertical: 'center', horizontal: 'center'}}>
            
                <Box style={{margin: 20, width: maxWidth}}>
                    <Typography variant="subtitle1" style={{fontWeight: 'bold', marginBottom: 20}}>Add to Calendar</Typography>  

                    {selectedSlotType ? 
                        <RadioGroup name="type" value={selectedSlotType.index} onChange={this._handleRadioChange} style={{marginTop: 20, marginBottom: 20}}>
                            {Object.values(NewSlotSelectionPopover.AllTypes).map((slotType, index) => 
                                <div key={index} style={{display: 'flex', justifyContent: 'left', gap: 20, alignItems: 'center'}}>
                                    <FormControlLabel  value={slotType.index} 
                                        control={<Radio color='primary'/>} 
                                        label={slotType.name} 
                                        style={{minWidth: 160}}
                                        disabled={!availableSlotTypes.includes(slotType)}
                                        />
                                    <Typography variant="body2" style={{color: 'gray', fontStyle: 'italic', flex: 1}}>{slotType.description}</Typography>
                                    {!availableSlotTypes.includes(slotType) ?
                                        <Typography variant="body2" style={{color: 'red', fontStyle: 'italic', textAlign: 'right', marginLeft: 'auto', marginRight: 20}}>
                                            {selectedResource.type === "Resource" ? "Select a Service instead" : selectedResource.type === "Service" ? "Select a Resource instead" : ""}
                                        </Typography>
                                        : null
                                    }
                                </div>)
                            }
                        </RadioGroup>
                        : null
                    }

                    <Autocomplete
                        id="resource"
                        style={{marginTop: 40}}
                        options={sortedResources}
                        getOptionLabel={(option) => option.type + ": " + option.name}
                        value={selectedResource}    
                        onChange={(event, value) => { this.setState({selectedResource: value}); }}
                        blurOnSelect
                        disableClearable
                        renderInput={(params) => <TextField {...params} label="Resource or Service" variant="outlined" InputLabelProps={{ shrink: true }} />}
                    />

                     
                    <Container style={this.styles.container}>    
                        <Button onClick={this._ok} color="primary" variant="outlined" style={this.styles.button}>Create</Button>
                        <Button onClick={this._cancel} variant="outlined" style={this.styles.button}>Cancel</Button>
                    </Container>
                </Box>

            </Popover>
        );
    }
}